/**
 * GLSL code for highlighting geometry based on ids passed in using geometry attributes.
 * @module
 */
import { HIGHLIGHT_TEX_WIDTH } from "../../HighlightDataTexture";

export const isolateModeDeclarationsVertex = `
attribute vec4 id;

#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
uniform bool isolateMode;

varying float vIsolateDiscard;
#endif

// Each id corresponds to a single pixel in this texture.
uniform sampler2D highlightedIds;
const float highlightedIdsSize = ${HIGHLIGHT_TEX_WIDTH.toFixed(1)};
const float highlightedIdsInvSize = 1.0 / highlightedIdsSize;

// This is the same as the algorithm in gpuPicking.ts.
float decodeId(vec4 encoded) {
  vec3 decodedBytes = floor((encoded.rgb * 255.0 - 64.0) * 0.33333333 + 0.5);
  return dot(decodedBytes, vec3(4096.0, 64.0, 1.0));
}

vec4 getEntityHighlightState() {
  float decoded = decodeId(id);
  vec2 coords = vec2(mod(decoded + 0.5, highlightedIdsSize), floor((decoded + 0.5) * highlightedIdsInvSize) + 0.5) * highlightedIdsInvSize;
  vec4 tex = texture2D(highlightedIds, coords);
  return tex;
}
`;

export const entityIdHighlightDeclarationsVertex = `
varying vec4 vEntitySelectionColor;

${isolateModeDeclarationsVertex}

// Uniforms have a cost, so we use constant colors.
const vec3 HIGHLIGHT_COLOR_BLUE = vec3(0.227, 0.502, 0.82);
const vec3 HIGHLIGHT_COLOR_BLUE_NOMIX = vec3(0.066, 0.376, 0.733);
const vec3 HIGHLIGHT_COLOR_PURPLE = vec3(0.82, 0.227, 0.82);
const vec3 HIGHLIGHT_COLOR_PURPLE_NOMIX = vec3(0.733, 0.066, 0.733);
`;

export const entityIdHighlightVertex = `
vec4 state = getEntityHighlightState();
vEntitySelectionColor.rgb = (state.y > state.x) ? HIGHLIGHT_COLOR_PURPLE : HIGHLIGHT_COLOR_BLUE;
vEntitySelectionColor.a = state.x + state.y;
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
bool isolated = state.w > 0.5;
vIsolateDiscard = isolateMode && !isolated ? 1.0 : 0.0;
#endif
gl_Position.z *= 1.0 + vEntitySelectionColor.a * 0.0002;
`;

export const entityIdHighlightVertexNoMix = `
vec4 state = getEntityHighlightState();
vEntitySelectionColor.rgb = (state.y > state.x) ? HIGHLIGHT_COLOR_PURPLE_NOMIX : HIGHLIGHT_COLOR_BLUE_NOMIX;
vEntitySelectionColor.a = min(max(state.x, state.y) * 4.0, 1.0);
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
bool isolated = state.w > 0.5;
vIsolateDiscard = isolateMode && !isolated ? 1.0 : 0.0;
#endif
gl_Position.z *= 1.0 + vEntitySelectionColor.a * 0.0001;
`;

export const entityIdHighlightVertexHighlightOnly = `
vec4 state = getEntityHighlightState();
vEntitySelectionColor.rgb = (state.y > state.x) ? HIGHLIGHT_COLOR_PURPLE_NOMIX : HIGHLIGHT_COLOR_BLUE_NOMIX;
vEntitySelectionColor.a = state.x * 1.15 + state.y * 1.15;
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
bool isolated = state.w > 0.5;
vIsolateDiscard = isolateMode && !isolated ? 1.0 : 0.0;
#endif
gl_Position.z *= 1.0 + vEntitySelectionColor.a * 0.0002;
`;

export const isolateModeVertex = `
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
vec4 state = getEntityHighlightState();
bool isolated = state.w > 0.5;
vIsolateDiscard = isolateMode && !isolated ? 1.0 : 0.0;
#endif
`;

export const isolateModeDeclarationsFragment = `
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
varying float vIsolateDiscard;
#endif
`;

export const entityIdHighlightDeclarationsFragment = `
varying vec4 vEntitySelectionColor;

${isolateModeDeclarationsFragment}
`;

export const entityIdHighlightFragment = `
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
if (vIsolateDiscard > 0.5) {
    discard;
} else {
#endif
  gl_FragColor.rgb = mix(gl_FragColor.rgb, vEntitySelectionColor.rgb, vEntitySelectionColor.a);
  gl_FragColor.a = max(gl_FragColor.a, 0.8 * vEntitySelectionColor.a);
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
}
#endif
`;

export const isolateModeFragment = `
#if defined(ISOLATE_MODE) && ISOLATE_MODE == 1
if (vIsolateDiscard > 0.5) {
  discard;
}
#endif
`;
